import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ClockCounterClockwise, MagnifyingGlass, SignIn, PencilSimple, Plus, Trash, CaretDown, ArrowCounterClockwise } from "@phosphor-icons/react";

const ACTION_STYLE = {
  "Autentificare": { cls: "bg-sky-500/15 text-sky-300", Icon: SignIn },
  "Creare": { cls: "bg-emerald-500/15 text-emerald-300", Icon: Plus },
  "Modificare": { cls: "bg-amber-500/15 text-amber-300", Icon: PencilSimple },
  "Ștergere": { cls: "bg-red-500/15 text-red-300", Icon: Trash },
};

export default function Audit() {
  const { hasRole } = useAuth();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [deleted, setDeleted] = useState([]);
  const [clearing, setClearing] = useState(false);

  const load = () => api.get("/audit").then((r) => setRows(r.data || [])).catch(() => setRows([]));
  const loadDeleted = () => api.get("/deleted-records").then((r) => setDeleted(r.data || [])).catch(() => setDeleted([]));
  useEffect(() => { load(); loadDeleted(); }, []);

  const clearAudit = async () => {
    if (!window.confirm("Ștergi TOATE înregistrările din jurnalul de acces (logurile)? Proiectele, utilizatorii și celelalte date NU sunt afectate. Acțiunea nu poate fi anulată.")) return;
    setClearing(true);
    try {
      const { data } = await api.delete("/audit");
      toast.success(`Jurnal șters (${data.deleted} înregistrări).`);
      load();
    } catch { toast.error("Nu s-a putut șterge jurnalul"); }
    finally { setClearing(false); }
  };

  const restore = async (rid) => {
    try { const { data } = await api.post(`/deleted-records/${rid}/restore`); toast.success(`Restaurat: ${data.restored || ""}`); loadDeleted(); }
    catch { toast.error("Nu s-a putut restaura"); }
  };
  const purge = async (rid) => {
    try { await api.delete(`/deleted-records/${rid}`); toast.success("Șters definitiv"); loadDeleted(); }
    catch { toast.error("Nu s-a putut șterge"); }
  };

  const usersInLog = Array.from(new Map((rows || []).map((r) => [r.user_id, r.user_name || r.user_email])).entries())
    .filter(([id]) => id);
  const nlow = q.trim().toLowerCase();
  const filtered = (rows || []).filter((r) => {
    if (nlow && ![r.user_name, r.user_email, r.action, r.resource, r.path].some((x) => (x || "").toLowerCase().includes(nlow))) return false;
    if (userFilter && r.user_id !== userFilter) return false;
    const day = (r.ts || "").slice(0, 10);
    if (dateFrom && day < dateFrom) return false;
    if (dateTo && day > dateTo) return false;
    return true;
  });

  return (
    <div className="mx-auto max-w-6xl px-6 py-8 fade-up" data-testid="audit-page">
      <div className="mb-6 flex items-center gap-2">
        <ClockCounterClockwise size={24} weight="bold" className="text-primary" />
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Control & securitate</p>
          <h1 className="font-head text-3xl font-extrabold tracking-tighter">Jurnal de acces</h1>
          <p className="text-sm text-muted-foreground">Cine s-a autentificat și ce anume a modificat (înainte → după).</p>
        </div>
      </div>

      {hasRole("admin") && (
        <div className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/[0.04] p-5" data-testid="clear-audit-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="flex items-center gap-2 font-head text-lg font-bold text-amber-300"><Trash size={18} weight="bold" /> Șterge jurnalul de acces</h2>
              <p className="mt-1 text-xs text-muted-foreground">Golește DOAR logurile de acces (cine s-a autentificat și ce a modificat). Proiectele, utilizatorii și celelalte date rămân neatinse.</p>
            </div>
            <Button variant="destructive" onClick={clearAudit} disabled={clearing} data-testid="clear-audit-btn">
              {clearing ? "Se șterge…" : "Șterge jurnalul de acces"}
            </Button>
          </div>
        </div>
      )}

      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="relative sm:col-span-2 lg:col-span-1">
          <MagnifyingGlass size={18} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Caută…" className="pl-10" data-testid="audit-search" />
        </div>
        <select value={userFilter} onChange={(e) => setUserFilter(e.target.value)} className="h-10 rounded-md border border-border bg-background px-3 text-sm" data-testid="audit-user-filter">
          <option value="">Toți utilizatorii</option>
          {usersInLog.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
        <div className="flex items-center gap-1.5">
          <label className="text-xs text-muted-foreground">De la</label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="audit-date-from" />
        </div>
        <div className="flex items-center gap-1.5">
          <label className="text-xs text-muted-foreground">Până la</label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="audit-date-to" />
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">Data și ora</th>
              <th className="px-4 py-3 text-left font-semibold">Utilizator</th>
              <th className="px-4 py-3 text-left font-semibold">Acțiune</th>
              <th className="px-4 py-3 text-left font-semibold">Secțiune</th>
              <th className="px-4 py-3 text-left font-semibold">Detalii</th>
            </tr>
          </thead>
          <tbody>
            {rows === null && <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">Se încarcă…</td></tr>}
            {rows !== null && filtered.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">Nicio înregistrare.</td></tr>}
            {filtered.map((r) => {
              const s = ACTION_STYLE[r.action] || ACTION_STYLE["Modificare"];
              const changes = r.changes || [];
              const open = openId === r.id;
              return (
                <React.Fragment key={r.id}>
                  <tr className="border-t border-border" data-testid={`audit-row-${r.id}`}>
                    <td className="whitespace-nowrap px-4 py-3 text-xs text-muted-foreground">{fmtDateTime(r.ts)}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{r.user_name || "—"}</div>
                      <div className="text-[11px] text-muted-foreground">{r.user_email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${s.cls}`}>
                        <s.Icon size={13} weight="bold" /> {r.action}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs">{r.resource || "—"}</td>
                    <td className="px-4 py-3 text-xs">
                      {changes.length > 0 ? (
                        <button onClick={() => setOpenId(open ? null : r.id)} className="inline-flex items-center gap-1 text-primary hover:underline" data-testid={`audit-detail-${r.id}`}>
                          <CaretDown size={13} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
                          {changes.length} {changes.length === 1 ? "câmp" : "câmpuri"}
                        </button>
                      ) : <span className="text-muted-foreground">—</span>}
                    </td>
                  </tr>
                  {open && changes.length > 0 && (
                    <tr className="border-t border-border bg-white/[0.02]" data-testid={`audit-changes-${r.id}`}>
                      <td colSpan={5} className="px-4 py-3">
                        <div className="space-y-1.5">
                          {changes.map((c, i) => (
                            <div key={i} className="flex flex-wrap items-center gap-2 text-xs">
                              <span className="rounded bg-white/5 px-1.5 py-0.5 font-semibold text-foreground/90">{c.field}</span>
                              {"from" in c && <><span className="text-red-300/80 line-through">{c.from || "(gol)"}</span><span className="text-muted-foreground">→</span></>}
                              <span className="text-emerald-300">{c.to}</span>
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {hasRole("admin") && (
        <div className="mt-10" data-testid="recovery-section">
          <div className="mb-3 flex items-center gap-2">
            <Trash size={20} weight="bold" className="text-amber-400" />
            <h2 className="font-head text-xl font-bold">Coș de recuperare ({deleted.length})</h2>
          </div>
          <p className="mb-3 text-xs text-muted-foreground">Proiecte/utilizatori șterși — poți restaura sau șterge definitiv (ca să nu încarci baza de date).</p>
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Șters la</th>
                  <th className="px-4 py-3 text-left font-semibold">Tip</th>
                  <th className="px-4 py-3 text-left font-semibold">Element</th>
                  <th className="px-4 py-3 text-left font-semibold">Șters de</th>
                  <th className="px-4 py-3 text-right font-semibold">Acțiuni</th>
                </tr>
              </thead>
              <tbody>
                {deleted.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground" data-testid="recovery-empty">Coșul de recuperare este gol.</td></tr>}
                {deleted.map((d) => (
                  <tr key={d.id} className="border-t border-border" data-testid={`recovery-row-${d.id}`}>
                    <td className="whitespace-nowrap px-4 py-3 text-xs text-muted-foreground">{fmtDateTime(d.ts)}</td>
                    <td className="px-4 py-3 text-xs">{d.resource}</td>
                    <td className="px-4 py-3 font-medium">{d.label}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{d.deleted_by}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => restore(d.id)} className="gap-1.5" data-testid={`recovery-restore-${d.id}`}>
                          <ArrowCounterClockwise size={14} /> Restaurează
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => purge(d.id)} className="gap-1.5 text-red-400 hover:text-red-300" data-testid={`recovery-purge-${d.id}`}>
                          <Trash size={14} /> Șterge definitiv
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
